import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { environment } from '@env';
import { CUSTOM_ERROR_MESSAGE } from '@shared/interceptors';
import {
  BusinessAccount,
  LimitUpdateRequest,
  LimitUpdateResponse,
  RoleLimitRawResponse,
  TransactionLimitItem,
  TransactionLimitRawItem,
  UpdateSubjectLimitsRequest,
} from '@shared/models';
import { BusinessAccountService } from '@shared/services';
import { BackendService } from '@shared/services/backend.service';

import { mapLimits } from './transaction-mapping-utils';

@Injectable({
  providedIn: 'root',
})
export class TransactionLimitsService {
  constructor(
    private backendService: BackendService,
    private businessAccountService: BusinessAccountService,
  ) {}

  private getSelectedBusinessAccountId(): Observable<BusinessAccount['id']> {
    return this.businessAccountService.getSelectedBusinessAccountId();
  }

  public getBusinessAccountsTransactionLimits(): Observable<TransactionLimitItem[]> {
    return this.businessAccountService.getSelectedBusinessAccount().pipe(
      switchMap((businessAccount) =>
        this.backendService
          .get<
            TransactionLimitRawItem[]
          >(`${environment.limitsDomainEndpoint}/business-accounts/${businessAccount!.id}/limits/utilisation?scopeType=${businessAccount!.parent ? 'PARENT_BA' : 'CHILD_BA'}&scopeId=${businessAccount!.id}`)
          .pipe(
            map((response) => mapLimits(response)),
            catchError((errorRes) => throwError(() => errorRes)),
          ),
      ),
    );
  }

  public getTransactionLimits(scopeId: string, scopeType: 'FA_KFA' | 'CUSTOMER' | 'USER'): Observable<TransactionLimitItem[]> {
    const queryParam = `?scopeType=${scopeType}&scopeId=${scopeId}`;

    return this.getSelectedBusinessAccountId().pipe(
      switchMap((businessAccountId) =>
        this.backendService
          .get<
            TransactionLimitRawItem[]
          >(`${environment.limitsDomainEndpoint}/business-accounts/${businessAccountId}/limits/utilisation${queryParam}`, { context: new HttpContext().set(CUSTOM_ERROR_MESSAGE, 'Unable to fetch Transaction Limits.') })
          .pipe(
            map((response) => {
              const limits = response.filter((item) => item.scopeType === scopeType && item.scopeId === scopeId);
              return mapLimits(limits);
            }),
            catchError((errorRes) => throwError(() => errorRes)),
          ),
      ),
    );
  }

  public updateTransactionLimits(req: LimitUpdateRequest[]): Observable<unknown> {
    if (req.length > 1) {
      const body: Record<string, Record<string, number | null>> = {};
      req.forEach((item) => {
        body[item.id] = { amount: item.amount, count: null };
      });
      return this.getSelectedBusinessAccountId().pipe(
        switchMap((businessAccountId) =>
          this.backendService
            .update<LimitUpdateResponse[]>(`${environment.limitsDomainEndpoint}/business-accounts/${businessAccountId}/limits`, body)
            .pipe(
              map((response) => response),
              catchError((errorRes) => throwError(() => errorRes)),
            ),
        ),
      );
    } else {
      const id = req[0].id;
      const body = { amount: req[0].amount };
      return this.getSelectedBusinessAccountId().pipe(
        switchMap((businessAccountId) =>
          this.backendService
            .update<LimitUpdateResponse[]>(`${environment.limitsDomainEndpoint}/business-accounts/${businessAccountId}/limits/${id}`, body)
            .pipe(
              map((response) => response),
              catchError((errorRes) => throwError(() => errorRes)),
            ),
        ),
      );
    }
  }

  public getRoleLimits(roleName: string): Observable<TransactionLimitItem[]> {
    return this.getSelectedBusinessAccountId().pipe(
      switchMap((businessAccountId) =>
        this.backendService
          .get<RoleLimitRawResponse>(
            `${environment.accountPermissionEndpoint}/v1/roles/${roleName}/limits?businessAccountId=${businessAccountId}`,
          )
          .pipe(
            map((response) => {
              const limits: TransactionLimitItem[] = [];

              if (!response.utilizations) {
                return limits;
              }

              // Iterate over currencies
              Object.keys(response.utilizations).forEach((currency) => {
                const currencyLimits = response.utilizations![currency];

                // Process each limit type (SINGLE, DAILY, WEEKLY, MONTHLY)
                (['SINGLE', 'DAILY', 'WEEKLY', 'MONTHLY'] as const).forEach((type) => {
                  const limit = currencyLimits[type];
                  if (limit) {
                    const limitDisplayNames: Record<string, string> = {
                      SINGLE: 'Single',
                      DAILY: '1 Day',
                      WEEKLY: '7 Days',
                      MONTHLY: '30 Days',
                    };

                    limits.push({
                      id: limit.limitId,
                      type: type.toLowerCase(),
                      label: limitDisplayNames[type] || type,
                      amount: limit.limitAmount,
                      usedAmount: limit.deductionsTotal || 0,
                      currency,
                      progress: limit.limitAmount ? ((limit.deductionsTotal || 0) / limit.limitAmount) * 100 : 0,
                    });
                  }
                });
              });

              return limits;
            }),
            catchError((errorRes) => throwError(() => errorRes)),
          ),
      ),
    );
  }

  public createOrUpdateRoleLimit(roleName: string, amount: number, currencyCode = 'USD'): Observable<unknown> {
    return this.getSelectedBusinessAccountId().pipe(
      switchMap((businessAccountId) =>
        this.backendService
          .post<unknown>(
            `${environment.accountPermissionEndpoint}/v1/roles/${roleName}/limits`,
            {
              businessAccountId,
              currencyCode,
              periods: {
                SINGLE: {
                  amount,
                },
              },
            },
            { context: new HttpContext().set(CUSTOM_ERROR_MESSAGE, 'Unable to save role limit.') },
          )
          .pipe(
            map((response) => response),
            catchError((errorRes) => throwError(() => errorRes)),
          ),
      ),
    );
  }

  public createOrUpdateSubjectLimit(
    subjectId: string,
    periods: UpdateSubjectLimitsRequest['periods'],
    currencyCode = 'USD',
  ): Observable<LimitUpdateResponse[]> {
    const payload: UpdateSubjectLimitsRequest = {
      subjectId,
      currencyCode,
      periods,
    };

    return this.backendService
      .post<LimitUpdateResponse[]>(`${environment.accountFlowEndpoint}/api/portal/v1/subjects/${subjectId}/limits`, payload)
      .pipe(
        map((response) => response),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }
}
